import './theme/preventi'
import { css, html, LitElement } from 'lit'
import { property, state } from 'lit/decorators.js'
import { BridgedFormValuesContainer, ContactFormValuesContainer } from '@icure/form/icure'
import { makeInterpreter } from '@icure/form/utils/interpreter'
import { Code, Field, FieldMetadata, Form, Group, Subform, Validator } from '@icure/form/components/model'
import { CardinalSdk, CodeStub, DecryptedContact, DecryptedForm, DecryptedPatient, DecryptedService, HealthcareParty, randomUuid, User } from '@icure/cardinal-sdk'
import { Suggestion, Version } from '@icure/form/generic'
import { getRevisionsFilter } from '@icure/form/utils/fields-values-provider'
import { normalizeCode } from '@icure/form/utils/code-utils'
import React from 'react'
import { createComponent } from '@lit/react'
import { deepEquals } from '../../helpers/equal'
import { getAge, getAgeDescription } from '../../helpers/date'
import { mistralSummarize, type MistralSummarizeParams } from '../../core/mistral'
import { populateFormTranslations, translate } from '../../helpers/translate'
import { cvScore2 } from '../../helpers/cv-score'

export class LitDecoratedForm extends LitElement {
  @property() renderer = 'form'
  @property() form!: Form
  @property() language?: string = 'fr'

  @property() sdkLoader!: Promise<CardinalSdk | undefined>
  @property() ownersProvider: (terms: string[], ids?: string[], specialties?: string[]) => Promise<Suggestion[]> = async () => []
  @property() optionsProvider: (language: string, codifications: string[], searchTerms: string[]) => Promise<Code[]> = async () => []
  @property() rootCardinalForm!: DecryptedForm
  @property() currentContact!: DecryptedContact
  @property() contactsHistory: DecryptedContact[] | undefined = undefined
  @property() patient!: DecryptedPatient
  @property() author: User | undefined
  @property() responsible: HealthcareParty | undefined
  @property() contactModified: (contact: DecryptedContact) => Promise<DecryptedContact> = async (c) => c
  @property() formAddedOrModified: (form: DecryptedForm) => Promise<DecryptedForm> = async (f) => f
  @property() formDeleted: (formId: string) => Promise<void> = async () => {
    /**/
  }

  private undoStack: BridgedFormValuesContainer[] = []
  private redoStack: BridgedFormValuesContainer[] = []

  @state() formValuesContainer: BridgedFormValuesContainer | undefined = undefined
  @state() observedForms: Record<string, DecryptedForm> = {}

  static get styles() {
    return css`
      .icure-text-field {
        display: block;
      }

      h2 {
        width: 100%;
        font-size: 2em;
        margin-top: 1em;
        margin-bottom: 0;
        font-family: 'Roboto', Helvetica, sans-serif;
      }

      * {
        box-sizing: border-box;
      }
    `
  }

  public undo() {
    if (!this.formValuesContainer) return
    if (this.undoStack.length > 0) {
      this.redoStack.push(this.formValuesContainer)
      const popped = this.undoStack.pop() as BridgedFormValuesContainer
      this.formValuesContainer = popped.synchronise()
    } else {
      console.log('undo stack is empty')
    }
  }

  public redo() {
    if (!this.formValuesContainer) return
    if (this.redoStack.length > 0) {
      this.undoStack.push(this.formValuesContainer)
      const popped = this.redoStack.pop() as BridgedFormValuesContainer
      this.formValuesContainer = popped.synchronise()
    } else {
      console.log('redo stack is empty')
    }
  }

  async firstUpdated() {
    //const sdk = await this.sdkLoader
    await populateFormTranslations(this.form)
    const contactFormValuesContainer = await ContactFormValuesContainer.fromFormsHierarchy(
      this.rootCardinalForm,
      this.currentContact,
      this.contactsHistory ?? [],
      (label, serviceId) =>
        new DecryptedService({
          label,
          id: serviceId ?? randomUuid(),
          created: +new Date(),
          modified: +new Date(),
          responsible: this.responsible?.id,
          author: this.author?.id,
        }),
      async () => [],
      async (parentId: string, anchorId: string, fti: string) => {
        const id = randomUuid()
        return new DecryptedForm({
          id: id,
          created: +new Date(),
          modified: +new Date(),
          formTemplateId: fti,
          parent: parentId,
          descr: anchorId /* TODO, this legacy iCure on Mac hack is used to anchor a form inside a form template. Fix it by introducing an anchorId in Form */,
        })
      },
      async (formId) => {
        delete this.observedForms[formId]
        this.formDeleted(formId)
      },
    )

    const extractFormulas = (
      fieldGroupOrSubForms: (Field | Group | Subform)[],
      property: (fg: Field) => string | undefined,
    ): {
      metadata: FieldMetadata
      revisionsFilter: (id: string, history: Version<FieldMetadata>[]) => (string | null)[]
      formula: string
    }[] =>
      fieldGroupOrSubForms.flatMap((fg) => {
        if (fg.clazz === 'group') {
          return extractFormulas(fg.fields ?? [], property)
        } else if (fg.clazz === 'field') {
          const formula = property(fg)
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          return formula
            ? [
                {
                  metadata: {
                    label: fg.label(),
                    tags: fg.tags?.map((id) => normalizeCode(new CodeStub({ id: id })) as unknown as Code),
                  },
                  revisionsFilter: getRevisionsFilter(fg),
                  formula,
                },
              ]
            : []
        } else {
          return []
        }
      }) ?? []

    if (!this.responsible?.id) {
      throw new Error('Responsible is not defined')
    }

    const initialisedFormValueContainer = await new BridgedFormValuesContainer(
      this.responsible?.id,
      contactFormValuesContainer,
      makeInterpreter(),
      undefined,
      () => {
        return extractFormulas(this.form.sections?.flatMap((f) => f.fields) ?? [], (fg) => fg.computedProperties?.['defaultValue'])
      },
      () => {
        return extractFormulas(this.form.sections?.flatMap((f) => f.fields) ?? [], (fg) => fg.computedProperties?.['value'])
      },
      () => {
        const form = this.form

        const extractValidators = (
          fgss: (Field | Group | Subform)[],
        ): {
          metadata: FieldMetadata
          validators: Validator[]
        }[] =>
          fgss.flatMap((fg) => {
            if (fg.clazz === 'group') {
              return extractValidators(fg.fields ?? [])
            } else if (fg.clazz === 'field') {
              const validators = fg.validators
              return validators?.length
                ? [
                    {
                      metadata: {
                        label: fg.label(),
                        tags: fg.tags?.map((id) => normalizeCode(new CodeStub({ id: id })) as unknown as Code),
                      },
                      validators,
                    },
                  ]
                : []
            } else {
              return []
            }
          }) ?? []

        return form ? extractValidators(form.sections?.flatMap((f) => f.fields) ?? []) : []
      },
      this.language,
      undefined,
      {
        patient: () => this.patient,
        language: () => this.patient?.languages?.[0] ?? this.language ?? 'fr',
        ageText: () => getAgeDescription,
        age: () => getAge,
        Promise: () => Promise,
        summarize: () => (domainOrParams: string | Partial<MistralSummarizeParams>, questionsArg?: [string, string][]) => {
          const lang = this.patient?.languages?.[0] ?? this.language ?? 'fr'
          const params: MistralSummarizeParams =
            typeof domainOrParams === 'string'
              ? {
                  domain: 'summary',
                  subject: this.patient?.id ?? 'unknown',
                  questions: (questionsArg ?? []).filter(([, r]) => !!r?.length),
                  language: lang,
                  formTemplate: this.form,
                }
              : {
                  domain: domainOrParams.domain!,
                  subject: domainOrParams.subject ?? this.patient?.id ?? 'unknown',
                  status: domainOrParams.status ?? 'unknown',
                  questions: (domainOrParams.questions ?? []).filter(([, r]) => !!r?.length),
                  language: domainOrParams.language ?? lang,
                  globalStatus: domainOrParams.globalStatus,
                  domainStatuses: domainOrParams.domainStatuses,
                  formTemplate: domainOrParams.formTemplate ?? this.form,
                }
          return mistralSummarize(params)
        },
        translate: () => translate,
        cvScore2: () => cvScore2,
      },
    ).init()

    this.formValuesContainer = initialisedFormValueContainer

    if (this.formValuesContainer.getContactFormValuesContainer().currentContact !== this.currentContact) {
      await this.contactModified(this.formValuesContainer.getContactFormValuesContainer().currentContact)
    }

    initialisedFormValueContainer.registerChangeListener((newValue) => {
      const fvc = this.formValuesContainer
      this.redoStack = []
      if (fvc) this.undoStack.push(fvc)
      this.formValuesContainer = newValue

      const toSave = this.formValuesContainer.getContactFormValuesContainer()
      console.log('Saving', toSave)
      // Save to the backend

      // Save to the backend
      const allForms = toSave.allForms()

      Promise.all(
        allForms.map(async (f) => {
          const form = f as DecryptedForm
          if (form.id && !deepEquals(this.observedForms[form.id], form)) {
            this.observedForms[form.id] = await this.formAddedOrModified(form)
          }
        }),
      )
        .then(() =>
          Promise.all(
            Object.values(this.observedForms ?? {}).map(async (f) => {
              if (f.id) {
                if (!allForms.some((af) => af.id === f.id) && this.observedForms[f.id]) {
                  this.formDeleted(f.id).catch(console.error)
                  delete this.observedForms[f.id]
                }
              }
            }),
          ),
        )
        .then(() => {
          const c = toSave.coordinatedContact()
          if (c && !deepEquals(this.currentContact, c)) {
            this.contactModified(c)
          }
        })
    })
  }

  render() {
    return html`
      <icure-form
        .form="${this.form}"
        labelPosition="above"
        renderer="form:tab"
        .readOnly="${false}"
        .displayMetadata="${false}"
        .language="${this.language}"
        .formValuesContainer="${this.formValuesContainer}"
        .ownersProvider="${this.ownersProvider.bind(this)}"
        .optionsProvider="${this.optionsProvider.bind(this)}"
        )
      ></icure-form>
    `
  }
}

console.warn('Create custom element for decorated-form')
customElements.define('decorated-form', LitDecoratedForm)

export const DecoratedForm = createComponent({
  tagName: 'decorated-form',
  elementClass: LitDecoratedForm,
  react: React,
  events: {},
})
